import React from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, Typography,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { erpApi } from "../../../api/erp";
import { formatDateTime, formatMoney, entityId } from "../../../utils/erp";
import WebProfitSpinner from "../../system/WebProfitSpinner";

// [WP-STABLE][CASH-WORKSHEET] La planilla es una proyección de movimientos ya auditados.
// No permite modificar importes originales ni sustituye la contabilidad ERP.
function csvCell(value) {
  let text = String(value ?? "");
  // Excel/Calc interpretan =,+,-,@ y caracteres de control como fórmulas.
  if (/^[\s\t\r\n]*[=+@-]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

function exportRows(rows, session) {
  const columns = ["Turno", "Movimiento", "Fecha", "Clase", "Concepto", "Medio", "Entrada", "Salida", "Estado", "Comprobante ERP"];
  const table = [columns, ...rows.map((m) => [
    session?.sessionNumber || "", m.cashMovementNumber || "", m.occurredAt || "",
    m.kind || "", m.description || "", m.method || "",
    m.direction === "in" ? m.amount : "", m.direction === "out" ? m.amount : "",
    m.status || "", String(m.erpMovementId || m.movementId || ""),
  ])];
  const csv = `\ufeff${table.map((line) => line.map(csvCell).join(";")).join("\r\n")}`;
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `planilla-caja-${session?.sessionNumber || "turno"}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

const KIND_LABELS = { opening: "Apertura", closing: "Cierre", in: "Ingreso", out: "Egreso", reversal: "Reversa", adjustment: "Ajuste" };

function SummaryCell({ title, value, currency, muted = false }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 900, opacity: muted ? .6 : 1 }}>{value === null || value === undefined ? "Oculto" : formatMoney(value, currency)}</Typography>
    </Paper>
  );
}

export default function CashWorksheetDialog({ projectId, cashSessionId, onClose }) {
  const [data, setData] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(50);
  const [loading, setLoading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => { setPage(0); setData(null); }, [cashSessionId]);
  const refresh = React.useCallback(async () => {
    if (!cashSessionId) return;
    try {
      setLoading(true);
      setError("");
      const result = await erpApi.cashSessionWorksheet(projectId, cashSessionId, { page: page + 1, pageSize });
      setData(result);
    } catch (err) { setError(err.message || "No se pudo consultar la planilla"); }
    finally { setLoading(false); }
  }, [projectId, cashSessionId, page, pageSize]);
  React.useEffect(() => { refresh(); }, [refresh]);

  const exportCompleteWorksheet = async () => {
    if (!cashSessionId) return;
    try {
      setExporting(true);
      setError("");
      const combined = [];
      // 100 páginas × 200 movimientos. El límite evita saturar al navegador
      // si un turno quedó abierto durante meses por un error operativo.
      let latestSession = data?.cashSession;
      let complete = false;
      for (let number = 1; number <= 100; number += 1) {
        const chunk = await erpApi.cashSessionWorksheet(projectId, cashSessionId, { page: number, pageSize: 200 });
        latestSession = chunk.cashSession;
        combined.push(...(chunk.cashMovements || []));
        if (!chunk.hasMore) { complete = true; break; }
      }
      if (!complete) throw new Error("El turno supera 20.000 movimientos. Exportá por páginas o solicitá el archivo al administrador.");
      exportRows(combined, latestSession);
    } catch (err) { setError(err.message || "No se pudo exportar la planilla"); }
    finally { setExporting(false); }
  };

  const session = data?.cashSession;
  const register = data?.register;
  const totals = data?.totals || {};
  const rows = data?.cashMovements || [];
  const currency = session?.currency || register?.currency || "ARS";
  return (
    <Dialog open={Boolean(cashSessionId)} onClose={onClose} fullWidth maxWidth="lg" aria-labelledby="wp-cash-worksheet-title">
      <DialogTitle id="wp-cash-worksheet-title" sx={{ fontWeight: 900 }}>Planilla de caja {session?.sessionNumber || ""}</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {loading && !data ? <Stack direction="row" spacing={1} alignItems="center"><WebProfitSpinner size={20} /><Typography>Cargando planilla…</Typography></Stack> : null}
          {session ? <>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography sx={{ fontWeight: 900 }}>{register?.name || "Caja"} {register?.branchName ? `· ${register.branchName}` : ""}</Typography>
                <Typography variant="body2" color="text.secondary">Responsable: {session.openedBy?.name || "Sin nombre"} · Apertura: {formatDateTime(session.openedAt)}</Typography>
                {session.closedAt ? <Typography variant="body2" color="text.secondary">Cierre: {formatDateTime(session.closedAt)} · {session.closedBy?.name || "Sin nombre"}</Typography> : null}
              </Box>
              <Chip color={session.status === "open" ? "success" : "default"} label={session.status === "open" ? "Turno abierto" : "Turno cerrado"} />
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={6} md={3}><SummaryCell title="Apertura declarada" value={session.openingAmount} currency={currency} /></Grid>
              <Grid item xs={6} md={3}><SummaryCell title="Ingresos" value={totals.incoming} currency={currency} /></Grid>
              <Grid item xs={6} md={3}><SummaryCell title="Egresos" value={totals.outgoing} currency={currency} /></Grid>
              <Grid item xs={6} md={3}><SummaryCell title="Esperado" value={totals.hidden ? null : totals.expected} currency={currency} muted={totals.hidden} /></Grid>
            </Grid>
            {session.status === "closed" ? <Alert severity={Number(session.difference || 0) === 0 ? "success" : "warning"}>
              Declarado: <strong>{formatMoney(session.declaredClosingAmount, currency)}</strong> · Diferencia: <strong>{formatMoney(session.difference, currency)}</strong> · {session.reviewStatus === "approved" ? "Aprobado" : session.reviewStatus === "observed" ? "Observado" : session.reviewStatus === "pending" ? "Pendiente de revisión" : "Sin revisión necesaria"}
              {session.reviewNotes ? ` · Revisión: ${session.reviewNotes}` : ""}
            </Alert> : null}
            {session.denominationCounts && Object.keys(session.denominationCounts).length ? <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={800}>Conteo de efectivo guardado en el cierre</Typography>
              <Typography variant="body2">{Object.entries(session.denominationCounts).filter(([, qty]) => Number(qty) > 0).map(([bill, qty]) => `${qty} × $${Number(bill).toLocaleString("es-AR")}`).join(" · ") || "Sin billetes"}{Number(session.otherCashAmount || 0) > 0 ? ` · Otros: ${formatMoney(session.otherCashAmount, currency)}` : ""}</Typography>
            </Paper> : null}
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Movimientos del turno ({data.total || 0})</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 390 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>Fecha</TableCell><TableCell>Comprobante</TableCell><TableCell>Concepto</TableCell><TableCell>Tipo</TableCell><TableCell align="right">Ingreso</TableCell><TableCell align="right">Egreso</TableCell></TableRow></TableHead>
                <TableBody>
                  {rows.map((row) => <TableRow key={entityId(row)} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(row.occurredAt)}</TableCell>
                    <TableCell>{row.cashMovementNumber}</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>{row.description || "—"}{row.status === "voided" ? " · Anulado" : ""}</TableCell>
                    <TableCell>{KIND_LABELS[row.kind] || row.kind}</TableCell>
                    <TableCell align="right" sx={{ color: "success.main", fontWeight: 700 }}>{row.direction === "in" ? formatMoney(row.amount, currency) : "—"}</TableCell>
                    <TableCell align="right" sx={{ color: "error.main", fontWeight: 700 }}>{row.direction === "out" ? formatMoney(row.amount, currency) : "—"}</TableCell>
                  </TableRow>)}
                  {!rows.length ? <TableRow><TableCell colSpan={6}>Todavía no hay movimientos en este turno.</TableCell></TableRow> : null}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={data.total || 0} page={page} onPageChange={(_event, p) => setPage(p)} rowsPerPage={pageSize} rowsPerPageOptions={[25, 50, 100, 200]} onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }} labelRowsPerPage="Movimientos por página" />
            <Typography variant="caption" color="text.secondary">La planilla respeta permisos, responsables y cierre ciego. La exportación completa reúne todas las páginas del turno (hasta 20.000 movimientos), sin modificar asientos ni movimientos.</Typography>
          </> : null}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button startIcon={<RefreshRoundedIcon />} disabled={loading || !cashSessionId} onClick={refresh}>Actualizar</Button>
        <Button startIcon={<DownloadRoundedIcon />} disabled={!rows.length || loading || exporting} onClick={() => exportRows(rows, session)}>Página actual</Button>
        <Button startIcon={<DownloadRoundedIcon />} variant="outlined" disabled={!rows.length || loading || exporting} onClick={exportCompleteWorksheet}>{exporting ? "Preparando CSV…" : "CSV completo"}</Button>
        <Button variant="contained" onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
